import { date as dateUtils } from 'quasar'
import { onlyTime } from 'src/services/filters/date'

const normalizeToMonthDay = (value) => {
  if (!value) return null
  const cleaned = String(value).trim().replace(/\//g, '-')
  const parts = cleaned.split('-')
  if (parts.length === 3) {
    const [, mm, dd] = parts
    return `${String(mm).padStart(2, '0')}-${String(dd).padStart(2, '0')}`
  }
  if (parts.length === 2) {
    const [mm, dd] = parts
    return `${String(mm).padStart(2, '0')}-${String(dd).padStart(2, '0')}`
  }
  return null
}

const getEnvExtraXmasDates = () => {
  try {
    const raw = process.env.XMAS_EXTRA_DATES
    if (!raw) return []
    return raw.split(',')
      .map(s => normalizeToMonthDay(s))
      .filter(Boolean)
  } catch (e) {
    return []
  }
}

const CHRISTMAS_DATES = new Set([
  '12-25',
  '12-26',
  ...getEnvExtraXmasDates()
])
const NEW_YEAR_DATE = '12-31'
const CHRISTMAS_LUNCH_LAST_TIME = '13:45'
const CHRISTMAS_LUNCH_WITH_NOTE = new Set(['12:00', '12:15', '12:30', '12:45', '13:00', '13:15', '13:30', '13:45'])
const CHRISTMAS_DINNER_TIMES = new Set(['16:30', '19:30'])
const CHRISTMAS_DINNER_WITH_NOTE = new Set(['16:30'])
const CHRISTMAS_MAX_GROUP_SIZE = 12
const CHRISTMAS_LUNCH_NOTE = 'tot 15.00'
const CHRISTMAS_DINNER_NOTE = 'tot 19.00'
const NEW_YEAR_LAST_TIME = '19:00'

const toNumber = (value) => {
  if (value === undefined || value === null) return undefined
  const result = typeof value === 'string' ? parseInt(value, 10) : value
  return Number.isNaN(result) ? undefined : result
}

const resolveDateStr = (rawDate, fallbackSlot) => {
  if (!rawDate && !fallbackSlot) return null
  let source = rawDate
  if (!source && fallbackSlot) {
    source = fallbackSlot.time
  }

  if (!source) return null

  let dateCandidate
  if (source instanceof Date) {
    dateCandidate = source
  } else if (typeof source === 'string' && !source.includes('T')) {
    // Parse date string as YYYY-MM-DD in local timezone
    const parts = source.split('-')
    if (parts.length === 3) {
      const year = parseInt(parts[0], 10)
      const month = parseInt(parts[1], 10) - 1 // JS months are 0-indexed
      const day = parseInt(parts[2], 10)
      dateCandidate = new Date(year, month, day)
    } else {
      dateCandidate = new Date(source)
    }
  } else {
    dateCandidate = new Date(source)
  }

  if (Number.isNaN(dateCandidate.getTime())) {
    return null
  }

  return dateUtils.formatDate(dateCandidate, 'MM-DD')
}

const cloneSlot = (slot) => ({ ...slot })

export const isXmasMonthDay = (monthDay) => CHRISTMAS_DATES.has(monthDay)

export const decorateTimeslotsForXmas2025 = (timeslots = [], { partySize } = {}) => {
  if (!Array.isArray(timeslots) || !timeslots.length) {
    return timeslots
  }

  const normalisedPartySize = toNumber(partySize)

  return timeslots.reduce((accumulator, slot) => {
    const dateStr = resolveDateStr(null, slot)

    if (!dateStr) {
      accumulator.push(cloneSlot(slot))
      return accumulator
    }

    // Check if THIS timeslot is on Christmas
    if (isXmasMonthDay(dateStr)) {
      // Block large groups on Christmas dates
      if (normalisedPartySize && normalisedPartySize > CHRISTMAS_MAX_GROUP_SIZE) {
        return accumulator
      }

      const timeString = onlyTime(slot.time)
      const isLunch = timeString <= CHRISTMAS_LUNCH_LAST_TIME
      const isRequiredDinnerSlot = CHRISTMAS_DINNER_TIMES.has(timeString)

      // Filter out non-lunch/dinner slots
      if (!isLunch && !isRequiredDinnerSlot) {
        return accumulator
      }

      const enhancedSlot = cloneSlot(slot)

      // Apply lunch notes
      if (CHRISTMAS_LUNCH_WITH_NOTE.has(timeString)) {
        enhancedSlot.end_time_note = CHRISTMAS_LUNCH_NOTE
      }

      // Apply dinner handling
      if (isRequiredDinnerSlot) {
        enhancedSlot.christmas_category = 'dinner'
        if (CHRISTMAS_DINNER_WITH_NOTE.has(timeString)) {
          enhancedSlot.end_time_note = CHRISTMAS_DINNER_NOTE
        }
      } else {
        enhancedSlot.christmas_category = 'lunch'
      }

      accumulator.push(enhancedSlot)
      return accumulator
    }

    // Check New Year's Eve
    if (dateStr === NEW_YEAR_DATE) {
      const timeString = onlyTime(slot.time)
      if (timeString > NEW_YEAR_LAST_TIME) {
        return accumulator
      }
      accumulator.push(cloneSlot(slot))
      return accumulator
    }

    // Not a special date - pass through unchanged
    accumulator.push(cloneSlot(slot))
    return accumulator
  }, [])
}

export default decorateTimeslotsForXmas2025
